<template>
  <footer ref="footerRef" class="site-footer">
    <div class="site-footer__transition-in"></div>
    <div class="site-footer__inner">
      <div ref="logoRef" class="site-footer__brand">
        <img
          src="/Imgs/digitalizando.svg"
          alt="Digitalizando"
          class="site-footer__logo"
        />
        <p class="site-footer__tagline">
          Estratégia e Tecnologia para a Próxima Geração de Negócios.
        </p>
      </div>

      <nav ref="navRef" class="site-footer__nav">
        <a href="#pilares" class="site-footer__nav-link">Soluções</a>
        <a href="#jornada" class="site-footer__nav-link">Metodologia</a>
        <a href="#cta" class="site-footer__nav-link">Contato</a>
      </nav>

      <div ref="socialRef" class="site-footer__social">
        <span class="site-footer__social-label">Conecte-se</span>
        <div class="site-footer__social-links">
          <a
            href="https://www.instagram.com/odigitalizando/"
            target="_blank"
            rel="noopener"
            class="site-footer__social-link"
            data-magnetic
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <rect x="2" y="2" width="20" height="20" rx="5" />
              <circle cx="12" cy="12" r="5" />
              <circle
                cx="17.5"
                cy="6.5"
                r="1.5"
                fill="currentColor"
                stroke="none"
              />
            </svg>
          </a>
          <a
            href="mailto:contato@odigitalizando.com"
            class="site-footer__social-link"
            data-magnetic
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <rect x="2" y="4" width="20" height="16" rx="2" />
              <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
            </svg>
          </a>
        </div>
      </div>
    </div>

    <div ref="copyrightRef" class="site-footer__copyright">
      © 2026 ODigitalizando. Todos os direitos reservados.
    </div>
  </footer>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const footerRef = ref(null);
const logoRef = ref(null);
const navRef = ref(null);
const socialRef = ref(null);
const copyrightRef = ref(null);

let ctx;

onMounted(() => {
  ctx = gsap.context(() => {
    const els = [
      logoRef.value,
      navRef.value,
      socialRef.value,
      copyrightRef.value,
    ];

    els.forEach((el, i) => {
      gsap.fromTo(
        el,
        { autoAlpha: 0, y: 20, filter: "blur(6px)" },
        {
          autoAlpha: 1,
          y: 0,
          filter: "blur(0px)",
          duration: 0.8,
          delay: i * 0.1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: footerRef.value,
            start: "top 90%",
            toggleActions: "play none none reverse",
          },
        }
      );
    });
  }, footerRef.value);
});

onUnmounted(() => {
  if (ctx) ctx.revert();
});
</script>

<style scoped>
.site-footer {
  position: relative;
  padding: 5rem 2rem 2rem;
  background: linear-gradient(to bottom, var(--color-bg-deep), #06061a);
  border-top: 1px solid var(--color-border-glass);
}

.site-footer__transition-in {
  position: absolute;
  top: -100px;
  left: 0;
  right: 0;
  height: 100px;
  background: linear-gradient(to bottom, transparent, var(--color-bg-deep));
  pointer-events: none;
}

.site-footer__inner {
  max-width: 1100px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 3rem;
  align-items: start;
}

.site-footer__brand {
  visibility: hidden;
}

.site-footer__logo {
  width: 160px;
  filter: brightness(1.5) contrast(1.1);
  margin-bottom: 1rem;
}

.site-footer__tagline {
  font-size: 0.85rem;
  color: var(--color-text-secondary);
  line-height: 1.6;
  max-width: 300px;
}

.site-footer__nav {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  visibility: hidden;
}

.site-footer__nav-link {
  font-size: 0.9rem;
  color: var(--color-text-secondary);
  transition: color 0.3s var(--ease-out-quart);
}

.site-footer__nav-link:hover {
  color: var(--color-accent);
}

.site-footer__social {
  visibility: hidden;
}

.site-footer__social-label {
  display: block;
  font-size: 0.85rem;
  color: var(--color-text-muted);
  margin-bottom: 0.75rem;
}

.site-footer__social-links {
  display: flex;
  gap: 0.75rem;
}

.site-footer__social-link {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid var(--color-border-glass);
  color: var(--color-text-secondary);
  transition: border-color 0.3s var(--ease-out-quart),
    color 0.3s var(--ease-out-quart), box-shadow 0.3s var(--ease-out-quart);
}

.site-footer__social-link:hover {
  border-color: var(--color-accent);
  color: var(--color-accent);
  box-shadow: 0 0 20px var(--color-accent-glow);
}

.site-footer__copyright {
  text-align: center;
  margin-top: 3rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--color-border-glass);
  font-size: 0.75rem;
  color: var(--color-text-muted);
  visibility: hidden;
}

@media (max-width: 768px) {
  .site-footer__inner {
    grid-template-columns: 1fr;
    text-align: center;
  }

  .site-footer__tagline {
    margin: 0 auto;
  }

  .site-footer__nav {
    align-items: center;
  }

  .site-footer__social {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
}
</style>
